import type { SubmissionReceipt } from '@/application/brief/BriefGateway';
import type { SubmitBriefError } from '@/application/brief/SubmitBrief';
import {
  BRIEF_STEPS,
  BriefDraft,
  type BriefDraftFields,
  type BriefErrors,
  type BriefField,
  type BriefStep,
} from '@/domain/brief/BriefDraft';

export type WizardStatus = 'editing' | 'submitting' | 'failed' | 'sent';

/** Message porté par l'étape entière, quand un choix manque. */
export type StepNotice = 'projectType' | 'scope' | null;

interface WizardState {
  readonly draft: BriefDraft;
  readonly stepIndex: number;
  readonly errors: BriefErrors;
  readonly notice: StepNotice;
  readonly status: WizardStatus;
  readonly receipt: SubmissionReceipt | null;
  readonly honeypot: string;
  /** Nombre de tentatives refusées : l'interface s'en sert pour guider le focus. */
  readonly rejectedAttempts: number;
}

const noticeFor = (step: BriefStep, errors: BriefErrors): StepNotice => {
  if (step === 'project' && errors.projectType) return 'projectType';
  if (step === 'scope' && (errors.budget ?? errors.timeline)) return 'scope';
  return null;
};

/**
 * Modèle de présentation du formulaire en étapes. Immuable : chaque action
 * renvoie un nouvel état, ce qui le rend trivial à tester et à brancher sur React.
 * Les erreurs n'apparaissent qu'après une tentative, puis s'effacent dès la correction.
 */
export class BriefWizard {
  private readonly state: WizardState;

  private constructor(state: WizardState) {
    this.state = state;
  }

  static start(): BriefWizard {
    return new BriefWizard({
      draft: BriefDraft.empty(),
      stepIndex: 0,
      errors: {},
      notice: null,
      status: 'editing',
      receipt: null,
      honeypot: '',
      rejectedAttempts: 0,
    });
  }

  get step(): BriefStep {
    return BRIEF_STEPS[this.state.stepIndex] ?? 'project';
  }

  get stepNumber(): number {
    return this.state.stepIndex + 1;
  }

  get totalSteps(): number {
    return BRIEF_STEPS.length;
  }

  get isLastStep(): boolean {
    return this.state.stepIndex === BRIEF_STEPS.length - 1;
  }

  get values(): BriefDraftFields {
    return this.state.draft.values;
  }

  get draft(): BriefDraft {
    return this.state.draft;
  }

  get errors(): BriefErrors {
    return this.state.errors;
  }

  get notice(): StepNotice {
    return this.state.notice;
  }

  get status(): WizardStatus {
    return this.state.status;
  }

  get receipt(): SubmissionReceipt | null {
    return this.state.receipt;
  }

  get honeypot(): string {
    return this.state.honeypot;
  }

  get rejectedAttempts(): number {
    return this.state.rejectedAttempts;
  }

  update<TField extends BriefField>(field: TField, value: BriefDraftFields[TField]): BriefWizard {
    const draft = this.state.draft.with(field, value);
    const { [field]: _cleared, ...errors } = this.state.errors;
    const stillMissing = this.state.notice ? noticeFor(this.step, draft.validate(this.step)) : null;
    return this.with({
      draft,
      errors,
      notice: stillMissing,
      status: this.state.status === 'failed' ? 'editing' : this.state.status,
    });
  }

  /** Vérifie un champ à la sortie (blur), pour signaler tôt une adresse mal saisie. */
  touch(field: BriefField): BriefWizard {
    if (this.state.draft.values[field] === '') return this;
    const error = this.state.draft.validate(this.step)[field];
    return error ? this.with({ errors: { ...this.state.errors, [field]: error } }) : this;
  }

  setHoneypot(value: string): BriefWizard {
    return this.with({ honeypot: value });
  }

  next(): BriefWizard {
    const errors = this.state.draft.validate(this.step);
    if (Object.keys(errors).length > 0) {
      return this.reject({ errors, notice: noticeFor(this.step, errors) });
    }
    return this.with({
      stepIndex: Math.min(this.state.stepIndex + 1, BRIEF_STEPS.length - 1),
      errors: {},
      notice: null,
    });
  }

  back(): BriefWizard {
    return this.with({
      stepIndex: Math.max(this.state.stepIndex - 1, 0),
      errors: {},
      notice: null,
    });
  }

  goTo(step: BriefStep): BriefWizard {
    const index = BRIEF_STEPS.indexOf(step);
    return index < this.state.stepIndex
      ? this.with({ stepIndex: index, errors: {}, notice: null })
      : this;
  }

  /** Prépare l'envoi : renvoie vers la première étape incomplète si besoin. */
  beginSubmit(): BriefWizard {
    for (const [index, step] of BRIEF_STEPS.entries()) {
      const errors = this.state.draft.validate(step);
      if (Object.keys(errors).length > 0) {
        return this.reject({
          stepIndex: index,
          errors,
          notice: noticeFor(step, errors),
          status: 'editing',
        });
      }
    }
    return this.with({ status: 'submitting' });
  }

  succeed(receipt: SubmissionReceipt): BriefWizard {
    return this.with({ status: 'sent', receipt });
  }

  fail(error: SubmitBriefError): BriefWizard {
    return error.kind === 'invalid'
      ? this.with({ status: 'editing', errors: error.errors }).beginSubmit()
      : this.with({ status: 'failed' });
  }

  private reject(patch: Partial<WizardState>): BriefWizard {
    return this.with({ ...patch, rejectedAttempts: this.state.rejectedAttempts + 1 });
  }

  private with(patch: Partial<WizardState>): BriefWizard {
    return new BriefWizard({ ...this.state, ...patch });
  }
}
